import { canonicalZone, isOnOrigin } from '@app/shared'
import { appKey } from '@app/shared/brand'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useRef,
} from 'react'
import { holdFeatures } from './features'
import { clearOpenDocs } from './open-docs'
import { currentFace, currentSite, siteOrigin } from './public-router'
import { MAGIC_LINK_PATH, returnPath } from './return-path'
import { type RouterOutputs, useTRPC } from './trpc'

export { MAGIC_LINK_PATH }

/** Who is signed in, as the server answers it; null is nobody. */
export type Me = RouterOutputs['auth']['me']
type StaffRole = NonNullable<Me>['roles'][number]['role']

type AuthValue = {
  me: Me
  loading: boolean
  /** Why a sign-in link did not work, for the screen that offers another. */
  signInError: string | null
  logout: () => Promise<void>
  /** Does the caller hold a role (admin implies all) at this org? */
  can: (spaceId: string, ...roles: StaffRole[]) => boolean
  /** Does the caller hold a fine-grained permission at this org? */
  hasPerm: (spaceId: string, permission: string) => boolean
  /** Any staff grant anywhere — gates staff-only UI that isn't org-specific. */
  isStaff: boolean
  /** Operates the platform — gates global config (integrations, new orgs). */
  isPlatformAdmin: boolean
}

const AuthContext = createContext<AuthValue | null>(null)

/**
 * Whether this tab has already sent this sign-in through the platform.
 *
 * The round trip below comes back through `/auth/verify` and would otherwise
 * start itself again, for ever. One flag, set before leaving, is what makes the
 * second pass a no-op.
 */
const PLATFORM_SESSION = appKey('sso', 'platform')

/**
 * Sign in at the platform too, so the session can be found from anywhere.
 *
 * Signing in at a product sets a cookie on that product's domain and nowhere
 * else — they are separate registrable domains — so nothing but that site can
 * tell this person is signed in. The platform is where every other site asks,
 * which only works if the platform has been told.
 *
 * So one sign-in becomes one round trip: hand this session to the platform,
 * which sets its own cookie and hands it straight back to where we were. Both
 * sessions carry the same sign-in group, so signing out of either ends both.
 *
 * It runs once per tab, and only on a product — the platform has no need to
 * tell itself.
 */
export function establishPlatformSession(
  handoff: {
    mutateAsync: (input: { to: string; next: string }) => Promise<{
      url: string
    }>
  },
  /** What to do instead, when this is not going anywhere. */
  otherwise: () => void = () => {},
) {
  if (currentSite() === 'platform' || currentSite() === 'org')
    return otherwise()
  /*
   * Arrived FROM the platform: it is the reason this session exists, so telling
   * it would be a round trip to say what it just said — and one that lands
   * somewhere other than where the person was going.
   */
  if (new URLSearchParams(window.location.search).get('via') === 'platform') {
    sessionStorage.setItem(PLATFORM_SESSION, '1')
    return otherwise()
  }
  if (sessionStorage.getItem(PLATFORM_SESSION)) return otherwise()
  sessionStorage.setItem(PLATFORM_SESSION, '1')

  const here = window.location
  /*
   * Where the platform should send us back to — a path on the PLATFORM, which
   * is why only its query is built here: it is the platform's own exchange,
   * pointed at this site. The trip out and the trip home are one mechanism,
   * aimed twice.
   *
   * `/auth/verify` is not a landing, so a sign-in that happened there returns
   * to where the link said it was going — its own `next`, while that still
   * names this origin — and otherwise to the root, letting this site decide
   * what that is.
   */
  const linked = new URLSearchParams(here.search).get('next')
  const ask = new URLSearchParams({
    to: here.origin,
    next:
      here.pathname === MAGIC_LINK_PATH && isOnOrigin(linked, here.origin)
        ? linked
        : returnPath(here),
    /*
     * This trip is an errand, not a sign-in: the session it is telling the
     * platform about is already here. Without saying so, the way home is
     * another token redeemed into a SECOND session at this site — same person,
     * same browser, two rows in the list of where they are signed in.
     */
    have: '1',
  })
  handoff
    .mutateAsync({
      to: siteOrigin('platform'),
      next: `/sso?${ask.toString()}`,
    })
    .then((r) => window.location.assign(r.url))
    // Nothing is lost if it fails: this session works where it was made, and
    // the only cost is that another site cannot find it.
    .catch(otherwise)
}

/**
 * Resolves the signed-in user and gates the app. Also handles the magic-link
 * landing: a `?token=…` in the URL (the emailed link points at
 * `/auth/verify?token=…`) is redeemed once, then stripped so a refresh can't
 * replay it.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const trpc = useTRPC()
  const qc = useQueryClient()
  /*
   * A product's public site holds no session and draws the same for everybody,
   * so it reads nobody as signed in — whatever cookie the host still carries —
   * and does not ask. Decided here, once, so no part of the page can read the
   * visitor differently from the rest.
   */
  const onPublicFace = currentFace() === 'public'
  const me = useQuery({
    ...trpc.auth.me.queryOptions(),
    enabled: !onPublicFace,
  })

  /* Written as this renders, so a route's guard below reads the person's
     features before anything they gate is drawn. */
  holdFeatures(me.data?.features ?? [])

  const handoff = useMutation(trpc.auth.handoff.mutationOptions())

  /*
   * Where this person's day starts, observed rather than asked for.
   *
   * A cadence has no meaning without it: a daily rollup bucketed in UTC arrives
   * at a different local hour for everybody and miscounts across a DST change.
   * Nobody should have to answer a question about timezones to receive mail in
   * the morning, and the browser already knows.
   *
   * Sent once per session, and the server keeps only the FIRST answer — so a
   * laptop opened abroad does not move somebody's digest hour, and a zone they
   * have settled is not revised by a page load.
   *
   * **Canonicalized before it is sent**, because what a browser reports is
   * whatever its own ICU calls the zone: several builds say `Asia/Calcutta`,
   * `Europe/Kiev` and `America/Buenos_Aires` and do not recognize the modern
   * spelling at all. Stored raw, those rows match no zone the platform holds,
   * so the person is unplaceable and their mail goes out on Shabbos — silently,
   * since nothing about the row looks wrong. One spelling in, one out.
   */
  const observeZone = useMutation(trpc.auth.observeTimeZone.mutationOptions())
  const zoneSent = useRef(false)
  useEffect(() => {
    if (!me.data || zoneSent.current) return
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone
    if (!zone) return
    zoneSent.current = true
    observeZone.mutate({ timeZoneId: canonicalZone(zone) })
  }, [me.data, observeZone.mutate])

  /*
   * Where a redemption goes when it is finished here.
   *
   * A REAL navigation, not a rewrite: the app router takes the address it was
   * created with, so a history entry written underneath it is ignored and the
   * router answers `/auth/verify` with its own landing — dropping wherever the
   * link was actually going. Loading the destination outright settles it, at
   * the one moment everything is being fetched fresh anyway.
   *
   * `next` is what a handoff carries, and it is read from THIS address rather
   * than from the response. Only a return that still names this origin after
   * the URL parser has resolved it: a leading slash is not enough, because
   * `\` is an authority separator. Nowhere named means the root, never back
   * here, because a redemption is not a page.
   */
  const land = () => {
    const next = new URLSearchParams(window.location.search).get('next')
    window.location.replace(
      isOnOrigin(next, window.location.origin) ? next : '/',
    )
  }

  const verify = useMutation(
    trpc.auth.verify.mutationOptions({
      /*
       * Signed in here; now let the platform know, so every other site of ours
       * can find this session without a second sign-in. That leaves the page —
       * so the landing below is what happens when it does NOT, and the two can
       * never both navigate.
       */
      onSuccess: () => establishPlatformSession(handoff, land),
      /*
       * A link that did not work stays put and says so.
       *
       * Landing somewhere else would take the sentence with it — an expired
       * link is the one moment somebody needs telling why nothing happened,
       * and the screen that says it is also the one that sends a new link.
       */
    }),
  )
  const logout = useMutation(
    trpc.auth.logout.mutationOptions({
      onSuccess: () => {
        // The sign-in is over, so the tab forgets that it told the platform:
        // the next one has to tell it again.
        sessionStorage.removeItem(PLATFORM_SESSION)
        clearOpenDocs()
        qc.invalidateQueries()
      },
    }),
  )

  /*
   * Redeem a magic link exactly once on mount — but ONLY on the magic-link
   * route.
   *
   * `?token=` is not unique to sign-in. A member's unsubscribe link carries one
   * too, and it is a different kind of token entirely. Redeeming on any path
   * meant every unsubscribe link was fed to `auth.verify`, failed, and then had
   * its token stripped from the URL by the handler below — so the preferences
   * page re-rendered with nothing and told the member their link was broken.
   * The one thing self-service opt-out cannot do is fail.
   *
   * Matching the path is what makes the two tokens distinguishable, since
   * neither is inspectable from the other's side.
   */
  const redeemed = useRef(false)
  useEffect(() => {
    // Once, and the ref says so rather than an empty deps array pretending the
    // body reads nothing. It also survives StrictMode's double-invoke, which an
    // empty array does not — a magic link is single-use, so redeeming it twice
    // burns it and the second call fails.
    if (redeemed.current) return
    if (window.location.pathname !== MAGIC_LINK_PATH) return
    // A link that lands on the public site is sent on to the app unspent,
    // rather than redeemed where nothing reads it.
    if (onPublicFace) return
    const token = new URLSearchParams(window.location.search).get('token')
    if (!token) return
    redeemed.current = true
    verify.mutate({ token })
  }, [verify, onPublicFace])

  const value: AuthValue = {
    me: me.data ?? null,
    loading: me.isLoading || verify.isPending,
    signInError: verify.error?.message ?? null,
    logout: async () => {
      await logout.mutateAsync()
    },
    can: (spaceId, ...roles) =>
      !!me.data?.roles.some(
        (r) =>
          r.spaceId === spaceId &&
          (r.role === 'admin' || roles.includes(r.role)),
      ),
    hasPerm: (spaceId, permission) =>
      !!me.data?.isPlatformAdmin ||
      !!me.data?.permissions.some(
        (p) => p.spaceId === spaceId && p.permission === permission,
      ),
    isStaff: (me.data?.roles.length ?? 0) > 0,
    isPlatformAdmin: !!me.data?.isPlatformAdmin,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
